import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Location,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
  useParams,
} from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import AppNavigation from './AppNavigation';
import HomeScreen from '../screens/HomeScreen';
import TransactionsScreen from '../screens/TransactionsScreen';
import PortfolioScreen from '../screens/PortfolioScreen';
import BillsScreen from '../screens/BillsScreen';
import EmailsScreen from '../screens/EmailsScreen';
import SettingsScreen from '../screens/SettingsScreen';
import CashFlowDetailsScreen from '../screens/CashFlowDetailsScreen';
import CashFlowTransactionsScreen from '../screens/CashFlowTransactionsScreen';
import SpendingSummaryScreen from '../screens/SpendingSummaryScreen';
import CategorySpendingDetailsScreen from '../screens/CategorySpendingDetailsScreen';
import AccountDetailsScreen from '../screens/AccountDetailsScreen';
import PortfolioAccountDetailsScreen from '../screens/PortfolioAccountDetailsScreen';
import CreditCardBillsScreen from '../screens/CreditCardBillsScreen';
import TransactionDetailsScreen from '../screens/TransactionDetailsScreen';
import SplitTransactionScreen from '../screens/SplitTransactionScreen';
import EmailDetailsScreen from '../screens/EmailDetailsScreen';
import type { Transaction } from '../types';
import { consumeAppNavigationIntent } from '../utils/navigationAnimation';
import {
  getRegisteredRouteScrollContainers,
  type RouteScrollContainer,
} from '../utils/routeScrollRegistry';
import './AnimatedAppRoutes.css';

const BASE_PATHS = new Set(['/', '/portfolio', '/transactions', '/bills']);

const isBasePath = (pathname: string) => BASE_PATHS.has(pathname);

const horizontalSafeAreaStyle = {
  paddingLeft: 'env(safe-area-inset-left)',
  paddingRight: 'env(safe-area-inset-right)',
};

const desktopContentSafeAreaStyle = {
  paddingRight: 'env(safe-area-inset-right)',
};

function AppRoute({
  activePath,
  children,
  isMobile,
}: {
  activePath: '/' | '/portfolio' | '/transactions' | '/bills';
  children: ReactNode;
  isMobile: boolean;
}) {
  return (
    <AppNavigation activePath={activePath} isMobile={isMobile}>
      {children}
    </AppNavigation>
  );
}

function TransactionsRouteStack({ isMobile }: { isMobile: boolean }) {
  return (
    <>
      <AppRoute activePath="/transactions" isMobile={isMobile}>
        <TransactionsScreen />
      </AppRoute>
      <Routes>
        <Route path=":transactionId/split" element={<SplitTransactionScreen />} />
        <Route path=":transactionId" element={<TransactionDetailsScreen />} />
      </Routes>
    </>
  );
}

const getTransactionRouteState = (locationState: unknown, transaction: Transaction) => ({
  ...(locationState && typeof locationState === 'object'
    ? (locationState as Record<string, unknown>)
    : {}),
  transaction,
});

function AccountDetailsRouteStack() {
  const { accountId } = useParams<{ accountId: string }>();
  const location = useLocation();
  const accountBasePath = `/accounts/${accountId ?? ''}`;
  const getTransactionDetailPath = useCallback(
    (transaction: Transaction) => `${accountBasePath}/transactions/${transaction.id}`,
    [accountBasePath]
  );
  const getTransactionSplitPath = useCallback(
    (transaction: Transaction) => `${accountBasePath}/transactions/${transaction.id}/split`,
    [accountBasePath]
  );
  const getTransactionNavigationState = useCallback(
    (transaction: Transaction) => getTransactionRouteState(location.state, transaction),
    [location.state]
  );

  return (
    <>
      <AccountDetailsScreen
        getTransactionDetailPath={getTransactionDetailPath}
        getTransactionSplitPath={getTransactionSplitPath}
        getTransactionNavigationState={getTransactionNavigationState}
      />
      <Routes>
        <Route
          path="transactions/:transactionId/split"
          element={(
            <SplitTransactionScreen
              getTransactionDetailPath={getTransactionDetailPath}
              getTransactionNavigationState={getTransactionNavigationState}
              fallbackDetailPath={(transactionId) => `${accountBasePath}/transactions/${transactionId}`}
              fallbackListPath={accountBasePath}
            />
          )}
        />
        <Route
          path="transactions/:transactionId"
          element={(
            <TransactionDetailsScreen
              getTransactionSplitPath={getTransactionSplitPath}
              getTransactionNavigationState={getTransactionNavigationState}
              fallbackListPath={accountBasePath}
            />
          )}
        />
      </Routes>
    </>
  );
}

function CashFlowTransactionsRouteStack() {
  const { flowType, year, month } = useParams<{
    flowType: string;
    year: string;
    month: string;
  }>();
  const location = useLocation();
  const cashFlowBasePath = `/cash-flow/transactions/${flowType ?? ''}/${year ?? ''}/${month ?? ''}`;
  const getTransactionDetailPath = useCallback(
    (transaction: Transaction) => `${cashFlowBasePath}/transactions/${transaction.id}`,
    [cashFlowBasePath]
  );
  const getTransactionSplitPath = useCallback(
    (transaction: Transaction) => `${cashFlowBasePath}/transactions/${transaction.id}/split`,
    [cashFlowBasePath]
  );
  const getTransactionNavigationState = useCallback(
    (transaction: Transaction) => getTransactionRouteState(location.state, transaction),
    [location.state]
  );

  return (
    <>
      <CashFlowTransactionsScreen
        getTransactionDetailPath={getTransactionDetailPath}
        getTransactionSplitPath={getTransactionSplitPath}
        getTransactionNavigationState={getTransactionNavigationState}
      />
      <Routes>
        <Route
          path="transactions/:transactionId/split"
          element={(
            <SplitTransactionScreen
              getTransactionDetailPath={getTransactionDetailPath}
              getTransactionNavigationState={getTransactionNavigationState}
              fallbackDetailPath={(transactionId) => `${cashFlowBasePath}/transactions/${transactionId}`}
              fallbackListPath={cashFlowBasePath}
            />
          )}
        />
        <Route
          path="transactions/:transactionId"
          element={(
            <TransactionDetailsScreen
              getTransactionSplitPath={getTransactionSplitPath}
              getTransactionNavigationState={getTransactionNavigationState}
              fallbackListPath={cashFlowBasePath}
            />
          )}
        />
      </Routes>
    </>
  );
}

type AnimationDirection = 'forward' | 'back';
type AnimationKind = 'slide' | 'fade';
type AnimationPhase = 'idle' | 'preparing' | 'animating';
type ScrollSnapshot = {
  containers: Array<{
    containerId: string;
    scrollTop: number;
    scrollLeft: number;
  }>;
};
type ViewTransitionLike = {
  finished: Promise<void>;
};
type DocumentWithViewTransition = Document & {
  startViewTransition?: (updateCallback: () => void) => ViewTransitionLike;
};

const getAnimationKind = (fromPath: string, toPath: string): AnimationKind => {
  const fromBase = isBasePath(fromPath);
  const toBase = isBasePath(toPath);
  return fromBase && toBase ? 'fade' : 'slide';
};

const getPrefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const parseTimeToMs = (value: string): number => {
  const trimmed = value.trim();
  if (trimmed.endsWith('ms')) {
    return Number.parseFloat(trimmed);
  }
  if (trimmed.endsWith('s')) {
    return Number.parseFloat(trimmed) * 1000;
  }
  return 0;
};

const getMaxTransitionTimeMs = (element: HTMLElement): number => {
  const style = window.getComputedStyle(element);
  const durations = style.transitionDuration.split(',').map(parseTimeToMs);
  const delays = style.transitionDelay.split(',').map(parseTimeToMs);
  const pairCount = Math.max(durations.length, delays.length);

  if (pairCount === 0) {
    return 0;
  }

  let maxTime = 0;
  for (let i = 0; i < pairCount; i += 1) {
    const duration = durations[i % durations.length] ?? 0;
    const delay = delays[i % delays.length] ?? 0;
    maxTime = Math.max(maxTime, duration + delay);
  }

  return maxTime;
};

const getScrollableContainers = (root: HTMLElement): RouteScrollContainer[] => {
  const containers = getRegisteredRouteScrollContainers(root);
  return containers.filter(
    ({ element }) =>
      element.scrollHeight - element.clientHeight > 1
      || element.scrollWidth - element.clientWidth > 1
  );
};

const getScrollSnapshot = (root: HTMLElement): ScrollSnapshot | null => {
  const scrollableContainers = getScrollableContainers(root);
  if (scrollableContainers.length === 0) {
    return null;
  }

  const containers = scrollableContainers
    .map(({ containerId, element }) => ({
      containerId,
      scrollTop: element.scrollTop,
      scrollLeft: element.scrollLeft,
    }))
    .filter(({ scrollTop, scrollLeft }) => scrollTop > 0 || scrollLeft > 0);

  if (containers.length === 0) {
    return null;
  }

  return {
    containers,
  };
};

const renderRoutes = (location: Location, isMobile: boolean) => (
  <Routes location={location}>
    <Route
      path="/"
      element={(
        <AppRoute activePath="/" isMobile={isMobile}>
          <div className="h-full" style={isMobile ? horizontalSafeAreaStyle : desktopContentSafeAreaStyle}>
            <HomeScreen />
          </div>
        </AppRoute>
      )}
    />
    <Route
      path="/portfolio"
      element={(
        <AppRoute activePath="/portfolio" isMobile={isMobile}>
          <PortfolioScreen />
        </AppRoute>
      )}
    />
    <Route path="/transactions/*" element={<TransactionsRouteStack isMobile={isMobile} />} />
    <Route
      path="/bills"
      element={(
        <AppRoute activePath="/bills" isMobile={isMobile}>
          <BillsScreen />
        </AppRoute>
      )}
    />
    <Route
      path="/emails"
      element={<EmailsScreen />}
    />
    <Route path="/settings" element={<SettingsScreen />} />
    <Route path="/cash-flow" element={<CashFlowDetailsScreen />} />
    <Route path="/cash-flow/transactions/:flowType/:year/:month/*" element={<CashFlowTransactionsRouteStack />} />
    <Route path="/spending-summary" element={<SpendingSummaryScreen />} />
    <Route path="/spending-summary/category/:tagName" element={<CategorySpendingDetailsScreen />} />
    <Route path="/portfolio/accounts/:accountId" element={<PortfolioAccountDetailsScreen />} />
    <Route path="/accounts/:accountId/credit-card-bills" element={<CreditCardBillsScreen />} />
    <Route path="/accounts/:accountId/*" element={<AccountDetailsRouteStack />} />
    <Route path="/emails/:emailId" element={<EmailDetailsScreen />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const AnimatedAppRoutes = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const isMobile = useIsMobile();
  const staticStageRef = useRef<HTMLDivElement | null>(null);
  const currentLayerRef = useRef<HTMLDivElement | null>(null);
  const scrollSnapshotsRef = useRef<Record<string, ScrollSnapshot>>({});
  const activeViewTransitionClassRef = useRef<string | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion);

  const [currentLocation, setCurrentLocation] = useState(location);
  const [direction, setDirection] = useState<AnimationDirection>('forward');
  const [animationKind, setAnimationKind] = useState<AnimationKind>('slide');
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>('idle');

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    setPrefersReducedMotion(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (activeViewTransitionClassRef.current) {
        document.documentElement.classList.remove(activeViewTransitionClassRef.current);
        activeViewTransitionClassRef.current = null;
      }
    };
  }, []);

  const saveSnapshotForLocationKey = useCallback((locationKey: string) => {
    const snapshotRoot = staticStageRef.current;
    if (!snapshotRoot) {
      return;
    }

    const snapshot = getScrollSnapshot(snapshotRoot);
    if (snapshot) {
      scrollSnapshotsRef.current[locationKey] = snapshot;
      const keys = Object.keys(scrollSnapshotsRef.current);
      if (keys.length > 80) {
        delete scrollSnapshotsRef.current[keys[0]];
      }
    } else {
      delete scrollSnapshotsRef.current[locationKey];
    }
  }, []);

  const restoreSnapshotForLocationKey = useCallback((root: HTMLElement | null, locationKey: string): boolean => {
    if (!root) {
      return false;
    }

    const snapshot = scrollSnapshotsRef.current[locationKey];
    if (!snapshot || snapshot.containers.length === 0) {
      return false;
    }

    const containerMap = new Map<string, HTMLElement>();
    getRegisteredRouteScrollContainers(root).forEach(({ containerId, element }) => {
      containerMap.set(containerId, element);
    });

    let shouldRetry = false;

    snapshot.containers.forEach(({ containerId, scrollTop, scrollLeft }) => {
      const target = containerMap.get(containerId);
      if (!target) {
        return;
      }

      const maxScrollTop = Math.max(0, target.scrollHeight - target.clientHeight);
      const maxScrollLeft = Math.max(0, target.scrollWidth - target.clientWidth);
      if (
        (scrollTop > 0 && maxScrollTop <= 0)
        || (scrollLeft > 0 && maxScrollLeft <= 0)
      ) {
        // Delay restoration until the target can actually scroll.
        shouldRetry = true;
        return;
      }

      const nextScrollTop = Math.min(scrollTop, maxScrollTop);
      const nextScrollLeft = Math.min(scrollLeft, maxScrollLeft);
      target.scrollTop = nextScrollTop;
      target.scrollLeft = nextScrollLeft;

      if (
        (scrollTop > 0 && Math.abs(target.scrollTop - nextScrollTop) > 1)
        || (scrollLeft > 0 && Math.abs(target.scrollLeft - nextScrollLeft) > 1)
      ) {
        shouldRetry = true;
      }
    });

    return !shouldRetry;
  }, []);

  const restoreSnapshotWithRetry = useCallback((locationKey: string) => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;

    const attemptRestore = () => {
      if (cancelled) {
        return;
      }

      const restored = restoreSnapshotForLocationKey(staticStageRef.current, locationKey);
      if (restored || attempts >= maxAttempts) {
        return;
      }

      attempts += 1;
      window.requestAnimationFrame(attemptRestore);
    };

    attemptRestore();

    return () => {
      cancelled = true;
    };
  }, [restoreSnapshotForLocationKey]);

  useEffect(() => {
    if (location.key === currentLocation.key) {
      return;
    }

    saveSnapshotForLocationKey(currentLocation.key);
    const nextDirection: AnimationDirection = navigationType === 'POP' ? 'back' : 'forward';
    const nextAnimationKind = getAnimationKind(currentLocation.pathname, location.pathname);
    const isSamePathname = currentLocation.pathname === location.pathname;
    const hasAppNavigationIntent = consumeAppNavigationIntent();
    const canAnimateForNavigationType =
      nextAnimationKind === 'fade' || navigationType !== 'REPLACE';
    const isAnimatedNavigation =
      hasAppNavigationIntent &&
      !isSamePathname &&
      canAnimateForNavigationType;

    if (!isAnimatedNavigation) {
      setAnimationKind(nextAnimationKind);
      setCurrentLocation(location);
      setAnimationPhase('idle');
      return;
    }

    const docWithTransition = document as DocumentWithViewTransition;
    if (typeof docWithTransition.startViewTransition === 'function') {
      const transitionClass =
        nextAnimationKind === 'fade'
          ? 'vt-route-fade'
          : nextDirection === 'back'
            ? 'vt-route-back'
            : 'vt-route-forward';

      if (activeViewTransitionClassRef.current) {
        document.documentElement.classList.remove(activeViewTransitionClassRef.current);
      }
      activeViewTransitionClassRef.current = transitionClass;
      document.documentElement.classList.add(transitionClass);

      setDirection(nextDirection);
      setAnimationKind(nextAnimationKind);
      setAnimationPhase('idle');

      const viewTransition = docWithTransition.startViewTransition(() => {
        flushSync(() => {
          setCurrentLocation(location);
        });
      });

      viewTransition.finished.finally(() => {
        if (activeViewTransitionClassRef.current === transitionClass) {
          document.documentElement.classList.remove(transitionClass);
          activeViewTransitionClassRef.current = null;
        }
      });
      return;
    }

    setDirection(nextDirection);
    setAnimationKind(nextAnimationKind);
    setCurrentLocation(location);
    setAnimationPhase('preparing');
  }, [currentLocation, location, navigationType, saveSnapshotForLocationKey]);

  const isAnimating = animationPhase !== 'idle';

  useLayoutEffect(() => {
    return restoreSnapshotWithRetry(currentLocation.key);
  }, [currentLocation.key, restoreSnapshotWithRetry]);

  useEffect(() => {
    if (animationPhase !== 'preparing') {
      return;
    }

    // Give the browser one frame to paint the initial "preparing" state
    // before switching to the animating class.
    const startTimer = window.setTimeout(() => {
      setAnimationPhase('animating');
    }, prefersReducedMotion ? 0 : 20);

    return () => {
      window.clearTimeout(startTimer);
    };
  }, [animationPhase, prefersReducedMotion]);

  useEffect(() => {
    if (animationPhase !== 'animating') {
      return;
    }

    const layer = currentLayerRef.current;
    if (!layer) {
      setAnimationPhase('idle');
      return;
    }

    let isFinished = false;
    const finishAnimation = () => {
      if (isFinished) {
        return;
      }
      isFinished = true;
      setAnimationPhase('idle');
    };

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== layer) {
        return;
      }
      if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') {
        return;
      }
      finishAnimation();
    };

    const handleTransitionCancel = (event: TransitionEvent) => {
      if (event.target !== layer) {
        return;
      }
      finishAnimation();
    };

    layer.addEventListener('transitionend', handleTransitionEnd);
    layer.addEventListener('transitioncancel', handleTransitionCancel);

    const transitionTimeMs = prefersReducedMotion ? 1 : getMaxTransitionTimeMs(layer);
    const safetyTimeoutMs = Math.max(80, Math.ceil(transitionTimeMs + 120));
    const safetyTimer = window.setTimeout(() => {
      finishAnimation();
    }, safetyTimeoutMs);

    return () => {
      layer.removeEventListener('transitionend', handleTransitionEnd);
      layer.removeEventListener('transitioncancel', handleTransitionCancel);
      window.clearTimeout(safetyTimer);
    };
  }, [animationPhase, prefersReducedMotion]);

  return (
    <div className="bg-background h-full app-routes-root">
      <div
        ref={staticStageRef}
        className={`route-stage route-stage-static ${isAnimating
          ? `route-stage-incoming route-stage-${animationKind === 'fade' ? 'fade' : direction} ${animationPhase === 'animating' ? 'is-animating' : ''}`
          : ''
          }`}
      >
        <div ref={currentLayerRef} className="route-layer route-layer-current">
          {renderRoutes(currentLocation, isMobile)}
        </div>
      </div>
    </div>
  );
};

export default AnimatedAppRoutes;
